import { useState } from 'react';
import useScrollAnimation from '../../hooks/useScrollAnimation';

interface FAQItem {
  question: string;
  answer: string;
}

interface FAQSectionProps {
  eyebrow?: string;
  headline: string;
  faqs: FAQItem[];
}

const FAQSection = ({ eyebrow = 'Common Questions', headline, faqs }: FAQSectionProps) => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const { ref, isVisible } = useScrollAnimation();

  return (
    <section className="bg-[#f7f4ef] py-24 lg:py-32">
      <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
        <div ref={ref} className={`anim-section ${isVisible ? 'is-visible' : ''} text-center mb-14`}>
          {eyebrow && (
            <p className="text-[#c8a951] text-xs uppercase tracking-[0.35em] font-semibold mb-4">{eyebrow}</p>
          )}
          <h2
            className="font-display text-4xl lg:text-5xl font-bold text-[#0a0a0a]"
            style={{ fontFamily: '"Playfair Display", serif' }}
          >
            {headline}
          </h2>
        </div>

        <div className="max-w-3xl mx-auto space-y-0">
          {faqs.map((faq, i) => (
            <FAQItem
              key={i}
              faq={faq}
              index={i}
              isOpen={openIndex === i}
              onToggle={() => setOpenIndex(openIndex === i ? null : i)}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

const FAQItem = ({
  faq,
  index,
  isOpen,
  onToggle,
}: {
  faq: FAQItem;
  index: number;
  isOpen: boolean;
  onToggle: () => void;
}) => {
  const { ref, isVisible } = useScrollAnimation();

  return (
    <div
      ref={ref}
      className={`anim-section ${isVisible ? 'is-visible' : ''} border-b border-[#0a0a0a]/12`}
      style={{ transitionDelay: `${index * 0.06}s` }}
    >
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between gap-4 py-6 text-left cursor-pointer group"
        aria-expanded={isOpen}
      >
        <span
          className={`font-display text-base lg:text-lg font-semibold transition-colors duration-200 ${
            isOpen ? 'text-[#c8a951]' : 'text-[#0a0a0a] group-hover:text-[#c8a951]'
          }`}
          style={{ fontFamily: '"Playfair Display", serif' }}
        >
          {faq.question}
        </span>
        <span
          className={`w-8 h-8 flex items-center justify-center border shrink-0 transition-all duration-300 ${
            isOpen
              ? 'border-[#c8a951] text-[#c8a951] rotate-45'
              : 'border-[#0a0a0a]/20 text-[#0a0a0a]/40 group-hover:border-[#c8a951] group-hover:text-[#c8a951]'
          }`}
        >
          <i className="ri-add-line text-sm" />
        </span>
      </button>

      <div
        className={`overflow-hidden transition-all duration-400 ease-in-out ${
          isOpen ? 'max-h-96 pb-6' : 'max-h-0'
        }`}
      >
        <p className="text-[#0a0a0a]/60 text-sm leading-relaxed border-l-2 border-[#c8a951] pl-5">
          {faq.answer}
        </p>
      </div>
    </div>
  );
};

export default FAQSection;
